import { AircraftState } from '../sim/AircraftState';
import { RunwayDef } from '../world/Airport';

/**
 * ILS navigation for the single runway: derives localizer and glideslope needle
 * deflections (in "dots", ±2 full scale) and DME distance to the threshold.
 */
export interface NavReadings {
  hasSignal: boolean;
  locDots: number; // + = course is to the right (fly right)
  gsDots: number; // + = glidepath is above (fly up)
  toFrom: 'TO' | 'FROM' | 'OFF';
  dme: number; // nautical miles to threshold
  bearingToStation: number; // deg true, for reference
}

const DEG = Math.PI / 180;
const M_PER_NM = 1852;

export function computeILS(state: AircraftState, rwy: RunwayDef): NavReadings {
  const dn = state.pos.n - rwy.threshold.n;
  const de = state.pos.e - rwy.threshold.e;

  // Along-track (positive = out on the approach, i.e. before the threshold) and
  // cross-track distances relative to the landing direction.
  const along = -(dn * rwy.dir.n + de * rwy.dir.e); // metres out from threshold
  const cross = dn * rwy.dir.e - de * rwy.dir.n; // + = right of centerline (facing landing dir)

  const horizRange = Math.hypot(dn, de);
  const dme = horizRange / M_PER_NM;

  // Only give a usable signal when roughly on the approach side and within range.
  const hasSignal = along > -300 && horizRange < 25 * M_PER_NM;

  // Localizer: angular deviation from centerline as seen from the threshold.
  const locRange = Math.max(Math.abs(along), 200);
  const locAngleDeg = (Math.atan2(cross, locRange) / DEG);
  const locDots = clamp((locAngleDeg / rwy.ils.locWidthDeg) * 2, -2.5, 2.5);

  // Glideslope: compare actual elevation angle to the target GS angle.
  const height = state.altitude - rwy.elevation;
  const gsRange = Math.max(along, 100);
  const actualGsDeg = Math.atan2(height, gsRange) / DEG;
  const gsErrorDeg = actualGsDeg - rwy.ils.gsAngleDeg; // + = above path
  const gsDots = clamp((gsErrorDeg / rwy.ils.gsWidthDeg) * 2, -2.5, 2.5);

  const bearingToStation = wrap360(Math.atan2(-de, -dn) / DEG);

  return {
    hasSignal,
    locDots: hasSignal ? locDots : 0,
    gsDots: hasSignal ? gsDots : 0,
    toFrom: hasSignal ? (along > 0 ? 'TO' : 'FROM') : 'OFF',
    dme,
    bearingToStation,
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
function wrap360(d: number): number {
  d %= 360;
  return d < 0 ? d + 360 : d;
}
